import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { useSEO } from '@/components/feature/useSEO';
import { testimonials, brandValues, milestones, features } from '@/mocks/data';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9u0vun8l97n3eas3fdg';

export default function AboutPage() {
  useSEO({
    title: '关于我们',
    description: '一起草是专为团队打造的多人协同文稿创作平台。了解我们的品牌故事、使命愿景、团队文化以及联系方式。我们致力于让每一次文稿协作都高效顺畅。',
    keywords: '一起草,关于我们,品牌介绍,协同创作平台,团队介绍,联系方式',
    canonicalUrl: 'https://utshealth.cn/about',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      'name': '关于一起草',
      'description': '一起草是专为团队打造的多人协同文稿创作平台。',
      'url': 'https://utshealth.cn/about',
      'isPartOf': {
        '@type': 'WebSite',
        'name': '一起草',
        'url': 'https://utshealth.cn',
      },
    },
  });

  const [contactStatus, setContactStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [contactError, setContactError] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });

  const handleContactSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setContactStatus('success');
      setFormData({ name: '', email: '', company: '', message: '' });
      return;
    }
    setContactStatus('loading');
    try {
      const fd = new FormData(form);
      const params = new URLSearchParams();
      fd.forEach((value, key) => { params.append(key, value.toString()); });
      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setContactStatus('error');
        setContactError(msg);
      }
    } catch {
      setContactStatus('error');
      setContactError('网络异常，请稍后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          {/* Hero */}
          <section className="text-center py-12 md:py-20">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
              关于一起草
            </span>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-6">
              让每一次
              <span className="text-primary-600">一起草</span>
              都高效顺畅
            </h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              一起草诞生于团队协作的真实痛点 —— 当一份合同需要法务、业务、管理层多方审阅时，反复传文件让效率大打折扣。
              我们决心打造一款真正好用的多人协同文稿创作工具，让团队把精力放在创作本身，而不是文件流转上。
            </p>
          </section>

          {/* Brand Values */}
          <section className="py-12 md:py-16 border-t border-background-200/70">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {brandValues.map((v) => (
                <div key={v.id} className="text-center p-8 bg-background-100 rounded-xl">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center bg-primary-100 text-primary-600 rounded-full mb-5">
                    <i className={`${v.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">{v.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Milestones */}
          <section className="py-12 md:py-16 border-t border-background-200/70">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-12">
              发展历程
            </h2>
            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2"></div>
              <div className="space-y-8">
                {milestones.map((m, idx) => (
                  <div key={idx} className={`relative flex items-start gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-12 md:pl-0`}>
                      <div className="hidden md:block">
                        <div className="text-sm font-bold text-primary-600 mb-1">{m.year}</div>
                        <p className="text-sm text-foreground-600">{m.event}</p>
                      </div>
                      <div className="md:hidden">
                        <div className="text-sm font-bold text-primary-600 mb-1">{m.year}</div>
                        <p className="text-sm text-foreground-600">{m.event}</p>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-500 rounded-full -translate-x-1/2 mt-1.5 ring-4 ring-background-50 z-10"></div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Features Highlight */}
          <section className="py-12 md:py-16 border-t border-background-200/70">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-12">
              为什么选择一起草
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f) => (
                <div key={f.id} className="flex gap-4 p-5 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg">
                    <i className={`${f.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">{f.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{f.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-12 md:py-16 border-t border-background-200/70">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-4">
              用户口碑
            </h2>
            <p className="text-foreground-500 text-sm text-center mb-10 max-w-xl mx-auto">
              来自真实用户的使用反馈，他们的认可是我们前进的最大动力
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="p-6 bg-background-100 rounded-xl">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(t.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-5">&ldquo;{t.content}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <img src={t.avatar} alt={t.name} className="w-9 h-9 rounded-full object-cover" />
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.role} · {t.company}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="py-12 md:py-16 border-t border-background-200/70">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-4">
              联系我们
            </h2>
            <p className="text-foreground-500 text-sm text-center mb-10 max-w-xl mx-auto">
              有任何问题或合作意向？欢迎随时联系我们，我们会在24小时内回复
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
              <div>
                {contactStatus === 'success' ? (
                  <div className="p-8 bg-background-100 rounded-xl text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-accent-500 flex items-center justify-center mb-4">
                      <i className="ri-check-line text-2xl text-background-50"></i>
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">消息已发送</h3>
                    <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复！</p>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} data-readdy-form className="p-6 bg-background-100 rounded-xl">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          id="contact-name"
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                          required
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          id="contact-email"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                          required
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-700 mb-1.5">公司/团队</label>
                        <input
                          id="contact-company"
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={(e) => setFormData((prev) => ({ ...prev, company: e.target.value }))}
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的公司或团队名称"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          value={formData.message}
                          onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入您想咨询的内容..."
                        />
                        <p className="text-xs text-foreground-400 mt-1">{formData.message.length}/500</p>
                      </div>
                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="anti-spam-field"
                      />
                      <button
                        type="submit"
                        disabled={contactStatus === 'loading'}
                        className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-colors text-sm whitespace-nowrap disabled:opacity-60"
                      >
                        {contactStatus === 'loading' ? '提交中...' : '发送消息'}
                      </button>
                      {contactStatus === 'error' && (
                        <p className="text-sm text-red-500 text-center">{contactError}</p>
                      )}
                    </div>
                  </form>
                )}
              </div>

              <div className="space-y-6">
                <div className="p-6 bg-background-100 rounded-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">邮箱</div>
                      <div className="text-sm text-foreground-500">support@utshealth.cn</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">电话</div>
                      <div className="text-sm text-foreground-500">400-888-9999</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">地址</div>
                      <div className="text-sm text-foreground-500">北京市海淀区中关村科技园</div>
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-background-100 rounded-xl">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3">工作时间</h3>
                  <div className="space-y-2 text-sm text-foreground-500">
                    <div className="flex justify-between">
                      <span>周一至周五</span>
                      <span>09:00 - 19:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span>周六</span>
                      <span>10:00 - 16:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span>周日</span>
                      <span>休息</span>
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-gradient-to-br from-primary-600 to-primary-800 rounded-xl text-background-50">
                  <h3 className="text-lg font-heading font-semibold mb-2">立即开始协作</h3>
                  <p className="text-sm text-background-50/80 mb-4">免费注册，体验高效协同文稿创作</p>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-50 text-primary-700 font-medium rounded-lg text-sm hover:bg-background-100 transition-colors whitespace-nowrap"
                  >
                    开始使用
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}